import React from "react";
import { Link } from "react-router-dom";
import Starfield from "@/components/Starfield";

export default function About() {
  return (
    <main className="relative">
      <Starfield density={24} dustDensity={10} />

      {/* HERO */}
      <section className="relative max-w-4xl mx-auto px-6 md:px-12 pt-16 pb-12 text-center">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-4">About the maker</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--rb-text-heading)] leading-tight">
          For the parents who know<br/><span className="italic">it&apos;s never just a dog.</span>
        </h1>
        <div className="ornament mt-8"><span>✦</span></div>
      </section>

      {/* THE STORY — her own words */}
      <section className="relative max-w-3xl mx-auto px-6 md:px-12 py-12">
        <div className="space-y-7 font-serif-display text-[19px] leading-[1.85] text-[var(--rb-text-heading)]">
          <p>
            I was inspired to make these dioramas because I started to make a memorial for the three furballs that transcended.
          </p>
          <p>
            <span className="italic">Princess</span> was my first love. She had a brother named <span className="italic">Little Bear</span>.
            And then there was my son&apos;s baby girl <span className="italic">Abby</span> — sweet AbbyGayos.
          </p>
          <p>
            I know the pain of losing a creature that made me feel so happy, made me laugh, and taught me unconditional love like no human ever did.
            As humans, we have expectations of our children too, which is perfectly normal.
            A furball is different.
          </p>

          <div className="my-10 py-8 border-y border-[var(--rb-gold)]/30 text-center">
            <div className="tracking-celestial text-[var(--rb-gold)] mb-4">In their own quiet way</div>
            <p className="font-serif-display italic text-[22px] md:text-[26px] leading-snug text-[var(--rb-text-heading)] max-w-2xl mx-auto">
              They don&apos;t judge.<br/>
              They don&apos;t expect.<br/>
              They don&apos;t demand.<br/>
              <span className="gold-text">They just love.</span><br/>
              They wait.<br/>
              They welcome you when you come home.<br/>
              They always miss you.
            </p>
          </div>

          <p>
            I have never seen my kids so devastated as when we lost AbbyGayos. As I&apos;ve been working to start this business,
            there hasn&apos;t been one day that I haven&apos;t had to stop to cry and process that love that remains strong.
          </p>
          <p>
            I understand those parents who suffer that pain and have to keep it within themselves — especially when dealing with
            other humans who don&apos;t understand this kind of love and call it <span className="italic">&ldquo;it&apos;s just a dog.&rdquo;</span>
          </p>
          <p className="text-[var(--rb-gold)] font-serif-display italic text-[22px] text-center pt-4">
            I want parents to feel understood, and seen.
          </p>
        </div>
      </section>

      {/* MICKEY & ABBY — the shelter day companion piece */}
      <section className="relative max-w-3xl mx-auto px-6 md:px-12 py-14">
        <div className="memorial-card p-10 md:p-14">
          <div className="text-center mb-6">
            <div className="tracking-celestial text-[var(--rb-gold)]">A quiet companion piece</div>
            <h2 className="font-serif-display italic text-4xl md:text-5xl text-[var(--rb-text-heading)] mt-2">
              Mickey &amp; Abby
            </h2>
            <div className="ornament mt-4"><span>✦</span></div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-[var(--rb-text-muted)] mt-2">The day at the shelter</div>
          </div>
          <div className="space-y-6 font-serif-display text-[18px] leading-[1.85] text-[var(--rb-text-heading)]">
            <p>
              We went to that shelter for one dog. Abby&apos;s little face online had already made the choice for us — she was coming home.
              But when we walked in, there was <span className="italic">Mickey</span>, curled up around her.
              Two tiny bodies tangled together, holding on to each other like they had been forming a single heart for a long time.
            </p>
            <p>
              No one had to speak the words. We looked at each other, and we said, <span className="italic">&ldquo;We&apos;re taking both of them.&rdquo;</span>
              There was no way we were going to break that bundle of love apart.
            </p>
            <p>
              Mickey came home too that day — <span className="italic">older sister. Steadier. Wilder. Funnier.</span>
              She would carry Abby through everything: through the years, through the quiet
              moments only sisters share. When Abby passed in 2020, Mickey stayed. She kept the porch light on for the rest of us.
              On August 11, 2026, she finally went to find her sister again.
            </p>
            <p className="text-[var(--rb-gold)] italic text-[20px] text-center pt-4">
              Run free, my sweet Mickstas.<br/>I know exactly who is waiting at the end of the path.
            </p>
          </div>
        </div>
      </section>

      {/* THE FOUNDING FAMILY */}
      <section className="relative max-w-6xl mx-auto px-6 md:px-12 py-20">
        <div className="text-center mb-12">
          <div className="tracking-celestial text-[var(--rb-gold)] mb-3">Our founding family</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--rb-text-heading)]">
            The souls who made this place possible.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { name: "Abby", role: "My son's baby", slug: "f8862626-abby", note: "Rat Terrier mix · 5 years. She waited at the stairs for him every single evening." },
            { name: "Mickey", role: "Abby's big sister", slug: "08112026-mickey", note: "Rat Terrier mix · 11 years. Wild, loving, and impossibly funny — the older sister who carried us all." },
            { name: "Little Bear", role: "Princess' brother", slug: "d50e0abf-little-bear", note: "Big, beautiful Border Collie mix. Onry, regal, and possessed of the most adorable babyish howl for my son." },
            { name: "Princess", role: "First love", slug: "e8f56ae1-princess", note: "Golden Lab Retriever · 11 years. Her belly-rub ritual will live in me forever." },
          ].map((p) => (
            <Link to={`/memorial/${p.slug}`} key={p.slug} className="memorial-card block p-8 text-center hover:-translate-y-1 transition-transform duration-500">
              <div className="tracking-celestial text-[var(--rb-gold)] text-[10px] mb-2">{p.role}</div>
              <div className="font-serif-display italic text-3xl text-[var(--rb-text-heading)]">{p.name}</div>
              <div className="ornament my-4"><span>✦</span></div>
              <p className="font-serif-display italic text-[15px] text-[var(--rb-text-body)] leading-relaxed">{p.note}</p>
              <div className="mt-6 text-[10px] tracking-[0.25em] uppercase text-[var(--rb-gold)] rainbow-underline inline-block">Visit their memorial →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* THE PROCESS */}
      <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="tracking-celestial text-[var(--rb-gold)] mb-3">Our process</div>
            <h2 className="font-serif-display text-4xl text-[var(--rb-text-heading)] leading-tight">
              Every diorama is<br/><span className="italic">hand-built by me.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6">
            {[
              { n: "I", t: "We talk about them", d: "We start with a conversation. Their name, their colors, the hour of the day they were happiest, the small ritual that was just yours." },
              { n: "II", t: "Their world is painted", d: "Using AI to visualize, we sketch their scene — a meadow, a hillside, a sunbeam they loved. We refine together until it feels like them." },
              { n: "III", t: "Their verse is written", d: "A heartfelt poem is composed specifically for them, in elegant typography, to be set inside the diorama." },
              { n: "IV", t: "It is built by hand", d: "I sculpt, paint, and assemble the diorama myself. Each tiny world is sealed under glimmering glass." },
              { n: "V", t: "It comes home to you", d: "Wrapped with care and posted to your door — an heirloom to live on your shelf, in the light, for years to come." },
            ].map((s) => (
              <div key={s.n} className="flex gap-5">
                <div className="font-serif-display italic text-[var(--rb-gold)] text-2xl flex-shrink-0 w-8">{s.n}.</div>
                <div>
                  <div className="font-serif-display text-xl text-[var(--rb-text-heading)] mb-1">{s.t}</div>
                  <p className="text-[15px] leading-[1.7] text-[var(--rb-text-body)]">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="memorial-card p-12 md:p-16 text-center">
            <div className="tracking-celestial text-[var(--rb-gold)] mb-4">When you&apos;re ready</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--rb-text-heading)] leading-tight">
              Let&apos;s make something<br/><span className="italic">worthy of them.</span>
            </h2>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link to="/create" className="btn-gold">Create a free memorial</Link>
              <Link to="/commission" className="btn-ghost">Commission a diorama</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
